import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { matchPath, useLocation } from "react-router-dom";

type RegistrationCredentials = { login: string; password: string };
type PendingPrefill = RegistrationCredentials & { expiresAt: number };
type RegistrationLoginPrefill = {
  prepare: (credentials: RegistrationCredentials) => void;
  consume: () => RegistrationCredentials | null;
};

const PREFILL_TTL_MS = 60000;
const PrefillContext = createContext<RegistrationLoginPrefill | null>(null);

// Credentials only bridge the registration -> login transition in this document.
// Never serialize them into router state, URLs, cookies, or browser storage.
export const RegistrationLoginPrefillProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const pendingRef = useRef<PendingPrefill | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    pendingRef.current = null;
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const prepare = useCallback((credentials: RegistrationCredentials) => {
    clear();
    pendingRef.current = { ...credentials, expiresAt: Date.now() + PREFILL_TTL_MS };
    timeoutRef.current = setTimeout(clear, PREFILL_TTL_MS);
  }, [clear]);

  const consume = useCallback(() => {
    const pending = pendingRef.current;
    clear();
    if (!pending || Date.now() >= pending.expiresAt) return null;
    return { login: pending.login, password: pending.password };
  }, [clear]);

  useLayoutEffect(() => {
    if (!matchPath("/auth", location.pathname)) clear();
  }, [location.key, location.pathname, clear]);

  useEffect(() => {
    window.addEventListener("pagehide", clear);
    return () => {
      window.removeEventListener("pagehide", clear);
      clear();
    };
  }, [clear]);

  const value = useMemo(() => ({ prepare, consume }), [prepare, consume]);
  return <PrefillContext.Provider value={value}>{children}</PrefillContext.Provider>;
};

export const useRegistrationLoginPrefill = () => {
  const context = useContext(PrefillContext);
  if (!context) throw new Error("RegistrationLoginPrefillProvider is required");
  return context;
};
